import DOMPurify from 'isomorphic-dompurify';
import { useEffect, useState } from 'react';

import '../../styles/generic.css';
import '../../styles/pricing-plan.css';

import { _ } from '../../utils/l10n';
import { getLargestElHeight } from '../../utils/getLargestElHeight';
import CheckIcon from '../../assets/icons/check.svg';
import PlusIcon from '../../assets/icons/plus-black.svg';
import { formatNumber } from '../../utils/formatters';
import { PRICING_CURRENCY } from '../../consts/currency';
import { ReadMoreButton } from '../generic/ReadMoreButton';

export type PricingItem = {
  /**
   * Pricing item name
   */
  name: string;
  /**
   * Pricing item price
   */
  price: number;
  /**
   * Pricing item min price
   */
  min_price?: number;
  /**
   * Indicates whether pricing item is recurring
   */
  is_recurring: boolean;
  /**
   * Indicates whether pricing item is highlighted
   */
  is_highlighted: boolean;
  /**
   * Indicates whether pricing item is percentage based
   */
  is_percentage_based: boolean;
  /**
   * Pricing item quotation display
   */
  quotation_display: string;
};

export type FeaturesGroup = {
  /**
   * Group title
   */
  title: string;
  /**
   * Group features
   */
  items: readonly string[];
}

export type PaidFeature = {
  /**
   * Paid feature name
   */
  name: string;
  /**
   * Paid feature price
   */
  price: number;
}

export interface PricingPlanTileProps {
  /**
   * Pricing plan title
   */
  title: string;
  /**
   * Pricing plan subtitle
   */
  subtitle?: string;
  /**
   * Pricing plan icon
   */
  icon: string;
  /**
   * Custom pricing plan subtitle
   */
  customSubtitle?: string;
  /**
   * Custom pricing plan text
   */
  customText?: string;
  /**
   * Pricing plan items
   */
  pricingItems: PricingItem[];
  /**
   * Pricing plan key features
   */
  keyFeatures: string[] | readonly FeaturesGroup[];
  /**
   * Pricing plan paid features
   */
  paidFeatures?: PaidFeature[];
  /**
   * Pricing suffix
   */
  pricingSuffix?: string;
}

/**
 * Pricing plan tile.
 *
 * Used also for "Cooperation Models" tiles.
 *
 * @todo refactor, introduce smaller components, extract into a more generic component (e.g. `OfferingTile`)
 */
export const PricingPlanTile = ({
  title,
  subtitle,
  customSubtitle,
  customText,
  pricingSuffix = 'excl. VAT',
  pricingItems,
  keyFeatures,
  paidFeatures,
  icon,
}: PricingPlanTileProps) => {
  const [txFeesHeight, setTxFeesHeight] = useState(100);
  const [expandKeyFeatures, setExpandKeyFeatures] = useState(false);

  const isCustom = customSubtitle && customText;
  const planPricing = pricingItems?.find((item) => item.is_recurring);
  const highlightedItems = pricingItems?.filter((item) => item.is_highlighted);

  useEffect(() => {
    setTxFeesHeight(getLargestElHeight('.pricing-plan-transaction-fees'));
  }, []);

  const getPriceDisplay = (price: number, isPercentageBased?: boolean) => {
    if (price === 0) {
      return <img src={CheckIcon} alt='Checkmark' className='check-icon' />;
    }

    if (isPercentageBased) {
      return `${price}%`;
    }

    return `${formatNumber(price)} ${PRICING_CURRENCY}`;
  };

  const getReadMoreItemClassName = (index: number) => index > 2 ? ` read-more-item${expandKeyFeatures ? '-open' : ''}` : '';
  const getKeyFeatureClassName = (index: number) => (
    `pricing-plan-key-feature${!highlightedItems?.length && !isCustom ? ' key-feature-bordered' : ''}${getReadMoreItemClassName(index)}`
  );

  return (
    <li className='tile pricing-plan-tile'>
      {/* Pricing Plan Header */}
      <header className='pricing-plan-tile-header'>
        <div>
          <h3
            className='title-3'
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }}
          />
          {!!subtitle && (
            <div
              className='pricing-plan-tile-text'
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(subtitle) }}
            />
          )}
        </div>
        <div className="pricing-plan-tile-icon">
          <img src={icon} alt={_('Pricing plan icon')} />
        </div>
      </header>
      <div>
        {/* Custom info for custom pricing plans */}
        {isCustom && (
          <div className='pricing-plan-tile-content pricing-plan-tile-features-header'>
            {customSubtitle}
            <span className='custom-pricing-plan-description'>{customText}</span>
          </div>
        )}

        {/* Subscription Fees */}
        {!!planPricing && (
          <div className='pricing-plan-tile-content'>
            <div>
              {planPricing?.min_price && (
                <div className='pricing-plan-tile-min-price pricing-plan-tile-text'>
                  {_('Starting at')}
                </div>
              )}
              <span className='pricing-plan-tile-price'>
                {(planPricing?.min_price || planPricing?.price)}
              </span>
              <span className='pricing-plan-tile-unit'> {PRICING_CURRENCY} </span>
              {pricingSuffix && <span className='pricing-plan-tile-text'>{pricingSuffix}</span>}
            </div>
            <div className='pricing-plan-tile-quotation pricing-plan-tile-text'>
              {planPricing?.quotation_display}
            </div>
          </div>
        )}

        {/* Transaction Fees for non custom pricing plans */}
        {highlightedItems?.length > 0 && (
          <div className='pricing-plan-transaction-fees' style={{ height: txFeesHeight }}>
            <h6 className='pricing-plan-tile-tx-fees-header pricing-plan-tile-text'>
              <img src={PlusIcon} alt='' />
              {_('Transaction fees')}
            </h6>
            <ul>
              {highlightedItems?.map((fee) => (
                <li key={fee.name} className="pricing-plan-transaction-fee pricing-plan-tile-text">
                  <div>{fee.name}</div>
                  <div>{!!fee.price && getPriceDisplay(fee.price, fee.is_percentage_based)}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Key Features */}
        {keyFeatures.length > 0 && (
          <div className='pricing-plan-tile-key-features'>
            {!highlightedItems?.length && (
              <h6 className='pricing-plan-tile-features-header'>
                {isCustom ? _('Additional features') : _('Includes')}
              </h6>
            )}
            <ul>
              {
                // @todo extract, refactor, dry
                keyFeatures?.map((keyFeature, index) => (
                  typeof keyFeature === 'string' ? (
                    // Single item
                    <li key={keyFeature} className={getKeyFeatureClassName(index)}>
                      <img src={CheckIcon} alt='' className='check-icon' />
                      <span
                        className='pricing-plan-tile-text'
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(keyFeature) }}
                      />
                    </li>
                  ) : (
                    // Group of items
                    <li
                      key={`group-${keyFeature.title}`}
                      className='pricing-plan-key-feature-group'
                    >
                      <h4
                        className="title-4"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(keyFeature.title) }}
                      />
                      <ul>
                        {keyFeature.items.map((item) => (
                          <li key={item} className='pricing-plan-key-feature' >
                            <img src={CheckIcon} alt='' className='check-icon' />
                            <span
                              className='pricing-plan-tile-text'
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item) }}
                            />
                          </li>
                        ))}
                      </ul>
                    </li>
                  )
                ))
              }
              {keyFeatures?.length > 3 && (
                <>
                  <ReadMoreButton isOpen={expandKeyFeatures} onClick={() => setExpandKeyFeatures(!expandKeyFeatures)} />
                  <div className='pricing-plan-separator' />
                </>
              )}
            </ul>
          </div>
        )}

        {/* Paid features */}
        {paidFeatures?.length && (
          <div className='pricing-plan-tile-paid-features'>
            <h6 className='pricing-plan-tile-features-header'>
              {_('Paid features')}
            </h6>
            <ul>
              {paidFeatures?.map(({ name, price }) => (
                <li key={name} className="pricing-plan-tile-text pricing-plan-paid-feature">
                  <div>{name}</div>
                  <div>{price && getPriceDisplay(price)}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </li>
  );
};
